import { Head } from "@inertiajs/react"
import {
  AtSign,
  Camera01,
  LinkExternal01,
  MusicNote01,
  Youtube,
} from "@untitledui/icons"
import { useEffect, useRef, useState } from "react"
import type { ComponentType, ReactNode } from "react"

import { SuggestionCard } from "@/components/admin/collabs/suggestion-card"
import { CollabCard } from "@/components/collabs/collab-card"
import { PlaylistCard } from "@/components/playlists"
import { Avatar } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { TabList, Tabs } from "@/components/ui/tabs"
import { AuthedShell } from "@/layouts/authed-shell"
import { settingsProfilePath } from "@/routes"
import type { Profile } from "@/types"

interface ProfileShowProps {
  profile: Profile
  viewer_is_owner: boolean
}

/**
 * ProfileShow is a page component for showing user's profile.
 */
export default function ProfileShow({
  profile,
  viewer_is_owner,
}: ProfileShowProps) {
  const hasContributingBriefs = profile.contributing_briefs.length
  const hasPlaylists = profile.playlists.length
  const hasTrackSuggestions = profile.track_suggestions.length

  const autoSelectTab = () => {
    if (hasContributingBriefs > 0) return "collabs-contributor"
    if (hasPlaylists > 0) return "playlists"
    if (hasTrackSuggestions > 0) return "track-suggestions"

    return "collabs-contributor"
  }

  return (
    <AuthedShell headerVariant="light">
      <Head title={profile.display_name} />

      <div className="editorial max-w-container mx-auto flex flex-col gap-10 px-4 py-10 lg:gap-12 lg:px-8 lg:py-14">
        <section className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-start sm:text-left">
          <Avatar
            name={profile.display_name}
            avatarUrl={profile.avatar_url}
            size="2xl"
            className="shrink-0 shadow-sm"
          />

          <div className="flex flex-col gap-3">
            <p className="text-tertiary text-xs font-medium tracking-wider uppercase">
              Contributor
            </p>

            <div className="flex flex-wrap items-baseline justify-center gap-x-2 sm:justify-start">
              <h1 className="text-primary text-display-sm font-semibold">
                {profile.display_name}
              </h1>
              {profile.pronouns && (
                <span className="text-tertiary text-md">
                  {profile.pronouns}
                </span>
              )}
            </div>

            {viewer_is_owner && (
              <div className="pt-1">
                <Button href={settingsProfilePath()} size="sm">
                  Edit profile
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Tag line */}
        <section>
          <p className="text-tertiary text-xs font-semibold tracking-wide uppercase">
            Tagline
          </p>
          <div className="bg-surface-grouped flex min-h-15 flex-col justify-center gap-1 rounded-xl p-2">
            {profile.tagline ? (
              <div>
                <p className="text-secondary text-lg">{profile.tagline}</p>
              </div>
            ) : (
              <div className="text-secondary text-md flex h-full w-full grow items-center justify-center">
                <p>Not provided yet.</p>
              </div>
            )}
          </div>
        </section>

        {/* About & Social link */}
        <section>
          <p className="text-tertiary text-xs font-semibold tracking-wide uppercase">
            About
          </p>
          <div className="bg-surface-grouped flex min-h-25 flex-col gap-3 rounded-xl p-2">
            {profile.bio ? (
              <div>
                <p className="text-secondary leading-relaxed">{profile.bio}</p>
              </div>
            ) : (
              <div className="text-secondary text-md flex h-full w-full grow items-center justify-center">
                <p>Not provided yet.</p>
              </div>
            )}

            <SocialLinkBadgeGroup
              links={Object.entries(profile.social_links)}
            />
          </div>
        </section>

        <Tabs
          orientation="horizontal"
          defaultSelectedKey={autoSelectTab()}
          className="min-h-100"
        >
          <TabList
            size="sm"
            type="underline"
            items={[
              {
                id: "collabs-contributor",
                label: "Collabs Contributor",
                badge: hasContributingBriefs || undefined,
              },
              {
                id: "track-suggestions",
                label: "Suggestions",
                badge: hasTrackSuggestions || undefined,
              },
              {
                id: "playlists",
                label: "Playlists",
                badge: hasPlaylists || undefined,
              },
            ]}
          />
          {/* Collabs Feature Contributor */}
          <Tabs.Panel
            id="collabs-contributor"
            className="bg-surface-grouped mt-2 flex flex-col gap-4 rounded-xl p-6"
          >
            <section className="flex flex-col gap-3">
              <div>
                <p className="text-tertiary text-xs font-semibold tracking-wide uppercase">
                  Featured on
                </p>
              </div>

              <ExpandableSection>
                {hasContributingBriefs > 0 ? (
                  <div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                      {profile.contributing_briefs.map((brief, key) => (
                        <CollabCard key={key} brief={brief} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-tertiary py-6 text-center text-sm">
                      {viewer_is_owner
                        ? "You haven't contributed to any collabs yet."
                        : "This user hasn't contributed to any collabs yet."}
                    </p>
                  </div>
                )}
              </ExpandableSection>
            </section>
          </Tabs.Panel>

          {/* Collabs Track Contributor */}
          <Tabs.Panel
            id="track-suggestions"
            className="bg-surface-grouped mt-2 flex flex-col gap-4 rounded-xl p-6"
          >
            <section className="flex flex-col gap-3">
              <div>
                <p className="text-tertiary text-xs font-semibold tracking-wide uppercase">
                  Track suggestions
                </p>
              </div>
              <ExpandableSection>
                {hasTrackSuggestions > 0 ? (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {profile.track_suggestions.map((suggestion, key) => (
                      <SuggestionCard key={key} suggestion={suggestion} />
                    ))}
                  </div>
                ) : (
                  <div>
                    <p className="text-tertiary py-6 text-center text-sm">
                      {viewer_is_owner
                        ? "You haven't suggested any tracks yet."
                        : "This user hasn't suggested any tracks yet."}
                    </p>
                  </div>
                )}
              </ExpandableSection>
            </section>
          </Tabs.Panel>

          {/* Playlists */}
          <Tabs.Panel
            id="playlists"
            className="bg-surface-grouped mt-2 flex flex-col gap-4 rounded-xl p-6"
          >
            <section className="flex flex-col gap-3">
              <div>
                <p className="text-tertiary text-xs font-semibold tracking-wide uppercase">
                  Shared Playlists
                </p>
              </div>
              <ExpandableSection>
                {hasPlaylists > 0 ? (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                    {profile.playlists.map((playlist, key) => (
                      <PlaylistCard key={key} playlist={playlist} />
                    ))}
                  </div>
                ) : (
                  <div>
                    <p className="text-tertiary py-6 text-center text-sm">
                      {viewer_is_owner
                        ? "You haven't published any playlists yet."
                        : "This user hasn't published any playlists yet."}
                    </p>
                  </div>
                )}
              </ExpandableSection>
            </section>
          </Tabs.Panel>
        </Tabs>
      </div>
    </AuthedShell>
  )
}

/**
 * An expandable wrapper component based <section/> for clipping children elements.
 */
const LIMIT_HEIGHT_CLIP_ITEM = "h-150"
const LIMIT_HEIGHT_CLIP_ITEM_PX = 600

function ExpandableSection({ children }: { children: ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [canExpand, setCanExpand] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const checkHeight = () => {
      setCanExpand(el.scrollHeight > LIMIT_HEIGHT_CLIP_ITEM_PX)
    }

    checkHeight()

    const resizeObserver = new ResizeObserver(checkHeight)
    resizeObserver.observe(el)

    return () => resizeObserver.disconnect()
  }, [])

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={contentRef}
        className={
          canExpand && !isExpanded
            ? `${LIMIT_HEIGHT_CLIP_ITEM} overflow-hidden`
            : undefined
        }
      >
        {children}
      </div>

      {canExpand && (
        <div className="flex justify-center">
          <Button
            size="sm"
            color="link-gray"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? "See less" : "See more"}
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * A Group wrapper fragment component for holding social link in badge.
 */
interface SocialLinkBadgeGroupProps {
  links: [string, string][]
}

function SocialLinkBadgeGroup({ links }: SocialLinkBadgeGroupProps) {
  const hasSocialLink = Object.keys(links).length > 0

  const SOCIAL_LINK_ICON: Record<
    string,
    ComponentType<{ className?: string }>
  > = {
    instagram: Camera01,
    youtube: Youtube,
    tiktok: MusicNote01,
    twitter: AtSign,
    x: AtSign,
    threads: AtSign,
  }

  const socialLinkIcon = (platform: string) =>
    SOCIAL_LINK_ICON[platform.toLowerCase()] ?? LinkExternal01

  const platformLabel = (platform: string) =>
    platform.charAt(0).toUpperCase() + platform.slice(1)

  return hasSocialLink ? (
    <div className="flex flex-row justify-start gap-1 rounded-xl">
      <ul className="flex flex-wrap gap-2 p-1">
        {links.map(([platform, handle], key) => {
          const SocialIcon = socialLinkIcon(platform)
          return (
            <li key={key}>
              <Badge size="sm" color="gray" type="pill-color">
                <span className="inline-flex items-center gap-1">
                  <SocialIcon className="size-3" aria-hidden="true" />
                  {platformLabel(platform)}: {handle}
                </span>
              </Badge>
            </li>
          )
        })}
      </ul>
    </div>
  ) : (
    <></>
  )
}
